import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, LockKeyhole } from "lucide-react";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Input, PinPad } from "./ui";
import { bioAvailable, registerBio, verifyBio, makePinLock, checkPin, saveLock, clearLock } from "./lock";

export function MLogin() {
  const { login, logout } = useAuth();
  const [f, setF] = useState({ login: "", password: "" });
  const [busy, setBusy] = useState(false);
  const reason = sessionStorage.getItem("np_logout_reason");
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(f.login.trim(), f.password);
      if (u.role !== "retailer") { await logout(); toast.error("This app is for retailers only. Please use the web portal."); }
      sessionStorage.removeItem("np_logout_reason");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen flex-col justify-end bg-[#0B1F33] text-white" data-testid="m-login">
      <div className="px-6 pb-10 pt-16">
        <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-white/10"><LockKeyhole className="h-7 w-7" /></div>
        <h1 className="font-display text-4xl font-bold leading-tight">Retailer<br />login</h1>
        <p className="mt-2 text-sm text-white/60">Same Login ID and password as the web portal.</p>
      </div>
      <form onSubmit={go} className="space-y-4 rounded-t-[32px] bg-[#F6F5F1] px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-7 text-[#0B1F33]">
        {reason && <div className="rounded-2xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="m-logout-reason">{reason}</div>}
        <Input label="Mobile / Login ID" value={f.login} onChange={(e) => setF({ ...f, login: e.target.value })} autoComplete="username" required data-testid="m-login-id" />
        <Input label="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} autoComplete="current-password" required data-testid="m-login-password" />
        <Btn type="submit" loading={busy} data-testid="m-login-submit">Login</Btn>
        <a href="/forgot-password" className="block text-center text-sm font-semibold text-slate-500" data-testid="m-forgot">Forgot password?</a>
        {/Android/i.test(navigator.userAgent) && !window.Capacitor?.isNativePlatform?.() && (
          <a href="/downloads/retailer-app.apk" download className="block text-center text-sm font-bold text-[#0B1F33] underline" data-testid="m-download-apk">Download Android app (APK)</a>
        )}
      </form>
    </div>
  );
}

export function LockSetup({ user, onDone }) {
  const [bio, setBio] = useState(false);
  const [step, setStep] = useState("choose");
  const [credId, setCredId] = useState(null);
  const [pin, setPin] = useState("");
  const [first, setFirst] = useState("");
  useEffect(() => { bioAvailable().then(setBio); }, []);
  useEffect(() => {
    if (pin.length !== 4) return;
    if (!first) { setFirst(pin); setPin(""); return; }
    if (pin !== first) { toast.error("PINs do not match, try again"); setFirst(""); setPin(""); return; }
    makePinLock(pin).then((p) => { saveLock(user.id, { ...p, bio: credId }); toast.success(credId ? "Fingerprint lock on" : "App lock PIN set"); onDone(); });
  }, [pin]); // eslint-disable-line
  const useBio = async () => {
    try { setCredId(await registerBio(user)); setStep("pin"); } catch { toast.error("Fingerprint / Face ID setup was cancelled"); }
  };
  if (step === "choose") {
    return (
      <div className="flex min-h-screen flex-col justify-center gap-4 bg-[#F6F5F1] px-6" data-testid="m-lock-setup">
        <h1 className="font-display text-3xl font-bold text-[#0B1F33]">Secure your app</h1>
        <p className="text-sm text-slate-600">Every time you open the app, it will ask for your fingerprint / Face ID or a 4-digit app PIN. Payments still need your MPIN.</p>
        {bio && <Btn onClick={useBio} data-testid="m-lock-setup-bio"><Fingerprint className="h-5 w-5" />Use fingerprint / Face ID</Btn>}
        <Btn variant="ghost" onClick={() => setStep("pin")} data-testid="m-lock-setup-pin">Set 4-digit app PIN</Btn>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen flex-col justify-center bg-[#F6F5F1] px-6" data-testid="m-pin-step">
      <h1 className="mb-1 text-center font-display text-2xl font-bold text-[#0B1F33]">{first ? "Confirm app PIN" : credId ? "Set a backup app PIN" : "Set app PIN"}</h1>
      <p className="mb-8 text-center text-sm text-slate-500">{credId ? "Used when fingerprint does not work" : "Do not use your MPIN here"}</p>
      <PinPad value={pin} onChange={setPin} testid="m-setup-pin" />
    </div>
  );
}

export function LockScreen({ user, cfg, onUnlock }) {
  const { logout } = useAuth();
  const [pin, setPin] = useState("");
  const [tries, setTries] = useState(0);
  const bio = async () => { try { await verifyBio(cfg.bio); onUnlock(); } catch { toast.error("Not recognised — use your app PIN"); } };
  useEffect(() => { if (cfg.bio) bio(); }, []); // eslint-disable-line
  useEffect(() => {
    if (pin.length !== 4) return;
    checkPin(cfg, pin).then((ok) => {
      if (ok) return onUnlock();
      const n = tries + 1;
      setTries(n); setPin("");
      if (n >= 5) { clearLock(user.id); logout(); toast.error("Too many wrong attempts. Please login again."); } else toast.error(`Wrong PIN (${5 - n} left)`);
    });
  }, [pin]); // eslint-disable-line
  return (
    <div className="flex min-h-screen flex-col justify-center bg-[#F6F5F1] px-6" data-testid="m-lock-screen">
      <div className="mb-8 text-center">
        <div className="font-display text-2xl font-bold text-[#0B1F33]">Hi, {user.name.split(" ")[0]}</div>
        <div className="text-sm text-slate-500">Enter app PIN to unlock</div>
      </div>
      <PinPad value={pin} onChange={setPin} testid="m-unlock-pin" />
      {cfg.bio && <button onClick={bio} className="mx-auto mt-6 flex items-center gap-2 text-sm font-bold text-[#0B1F33]" data-testid="m-unlock-bio"><Fingerprint className="h-6 w-6" />Use fingerprint / Face ID</button>}
      <button onClick={() => { clearLock(user.id); logout(); }} className="mx-auto mt-6 text-xs font-semibold text-slate-500" data-testid="m-unlock-logout">Forgot PIN? Logout</button>
    </div>
  );
}
